import { MasterRepository } from 'application/repositories/MasterRepository';
import { PostPutDeleteBaseResponse } from 'domain/entities/CommonEntity';
import {
    MasterAreaResponse,
    MasterCompaniesResponse,
    MasterLinesResponse,
    MasterStationsResponse,
    MasterStationsWithSearchKeyResponse,
    MasterSubCategoryData,
    MasterWebAppUrl,
    SystemSettingsData,
} from 'domain/entities/MasterEntity';
import axiosConfig from 'infrastructure/config/axios';
import {
    DetailConfirmPropertyResponse,
    DetailPropertyConfirmPayload,
    DetailPropertyPayload,
    DetailPropertyResponse,
} from 'presentation/pages/CU/CU-062-01/models/CU-062-01.model';
import { commonHelpers } from 'utilities/helpers';

export class MasterAPI implements MasterRepository {
    async getMasterAreasSearchAreas(params: {
        page: number;
        pageSize?: number;
    }): Promise<MasterAreaResponse> {
        const API_URL = '/master/areas/search-areas';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }
    async getMasterAreasSearchAreasForSearch(params: {
        page: number;
        pageSize?: number;
    }): Promise<MasterAreaResponse> {
        const API_URL = '/master/areas/search-areas-auction';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }

    async getMasterAreasFitList(params: {
        areaId: number;
        level: number;
        page: number;
        pageSize?: number;
    }): Promise<MasterAreaResponse> {
        const API_URL = '/master/areas/fit/list';
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response?.data;
    }

    async getMasterSubCategoryList(params: {
        categoryCode: string;
    }): Promise<MasterSubCategoryData> {
        const API_URL = '/master/sub-category/list';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }

    async getMasterSubCategoryGroupList(params: {
        categoryCodes: string[];
    }): Promise<MasterSubCategoryData> {
        const API_URL = '/master/sub-category/group';
        const paramUrl = `?categoryCodes=${params.categoryCodes.join(',')}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL + paramUrl);
        return response?.data;
    }

    async getSystemSettingList(params: { keys: string[] }): Promise<SystemSettingsData> {
        const API_URL = '/master/sub-category/system-setting';
        const paramUrl = `?keys=${params.keys.join(',')}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL + paramUrl);
        return response?.data;
    }

    async putMasterPropertyRegisterListingInformation(data: {
        mstPropertyId: number;
    }): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/master/property/register-listing-information';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async getListedPropertyFilter(params: {
        page: number;
        pageSize?: number;
    }): Promise<MasterAreaResponse> {
        const API_URL = '/master/areas/search-areas';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }

    async getMasterCompaniesList(params: {
        areaId: number;
        sortBy: string;
        keyword?: string;
        page?: number;
        pageSize?: number;
    }): Promise<MasterCompaniesResponse> {
        const API_URL = '/master/companies/list'; //?areaId=10001&sortBy=company_cd
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response?.data;
    }

    async getMasterLinesList(params: {
        id: number;
        areaId: number;
        sortBy: string;
        keyword?: string;
        page?: number;
        pageSize?: number;
    }): Promise<MasterLinesResponse> {
        const API_URL = '/master/lines/list'; //?id=1&areaId=10001&sortBy=line_cd
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response?.data;
    }
    async getMasterStationsList(params: {
        id: number;
        areaId: number;
        keyword?: string;
        page?: number;
        pageSize?: number;
    }): Promise<MasterStationsResponse> {
        const API_URL = '/master/stations/list'; //?id=1&areaId=10001
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response?.data;
    }
    async getMasterWepAppUrl(type: number): Promise<MasterWebAppUrl> {
        const API_URL = `/mypage/web-view/${type}?isLogin=0`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response?.data;
    }
    async getSettingProperty(params: {
        settlementId: number;
        scheduleAdjustmentDiv: number;
    }): Promise<DetailPropertyResponse> {
        const API_URL = '/setting/property';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }
    async getListOptionTime(): Promise<any> {
        const API_URL = '/setting/list-option-time';
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response?.data;
    }
    async updateSettingProperty(payload: DetailPropertyPayload): Promise<any> {
        const API_URL = '/setting/property';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, payload);
        return response?.data;
    }
    async getConfirmSettingProperty(
        params: DetailPropertyConfirmPayload,
    ): Promise<DetailConfirmPropertyResponse> {
        const API_URL = '/setting/property-confirm';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response?.data;
    }
    async confirmSettingProperty(payload: DetailPropertyConfirmPayload): Promise<any> {
        const API_URL = '/setting/property-confirm';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, payload);
        return response?.data;
    }
    async getMasterStationByKeys(key: any): Promise<MasterStationsWithSearchKeyResponse> {
        const API_URL = '/master/stations/search?isLogin=0';
        const formData = commonHelpers.convertObjectToFormData({ keyword: key });
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response.data;
    }
}
